// Browser client module — factory function for Spectre protocol operations

import { generateKeypair, getPublicKey, hash, concat } from '../core/crypto.js';
import { createRoom, advanceHead, generateRoomId, type RoomState } from '../core/room.js';
import { sendMessage, receiveMessage } from '../core/protocol.js';
import { type Result, ok, err } from '../core/types.js';
import { POW_BITS } from '../core/constants.js';
import { createActor } from 'xstate';

// Re-export machines for the browser bundle
export { identityMachine } from './machines/identity.js';
export { roomMachine } from './machines/room.js';
export { connectionMachine } from './machines/connection.js';

// Re-export xstate createActor for app.js
export { createActor } from 'xstate';

// Re-export core functions the app.js needs for decryption
export { createRoom, advanceHead, generateRoomId, type RoomState } from '../core/room.js';
export { receiveMessage } from '../core/protocol.js';

// --- Hex encoding utilities ---

const toHex = (bytes: Uint8Array): string =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

const fromHex = (hex: string): Uint8Array =>
  Uint8Array.from(
    { length: hex.length / 2 },
    (_, i) => parseInt(hex.slice(i * 2, i * 2 + 2), 16),
  );

const computeFingerprint = (publicKeyBytes: Uint8Array): string =>
  toHex(hash(publicKeyBytes).slice(0, 8));

// --- Client config ---

type ClientConfig = Readonly<{
  serverUrl: string;
  powBits?: number;
}>;

// --- Share code encoding ---

type SharePayload = Readonly<{
  roomId: string;
  sharedSecret: string;
  members: ReadonlyArray<string>;
  currentHead: string;
}>;

const encodeShareCode = (payload: SharePayload): string =>
  btoa(JSON.stringify(payload));

const decodeShareCode = (shareCode: string): Result<SharePayload, string> => {
  try {
    const parsed = JSON.parse(atob(shareCode)) as SharePayload;
    if (!parsed.roomId || !parsed.sharedSecret || !Array.isArray(parsed.members) || !parsed.currentHead) {
      return err('Invalid share code structure');
    }
    return ok(parsed);
  } catch {
    return err('Failed to decode share code');
  }
};

// --- Status response type ---

type ServerStatus = Readonly<{
  version: number;
  protocol_version: number;
  pow_bits: number;
  max_message_size: number;
  message_count: number;
  uptime_seconds: number;
}>;

// --- Stored message from server ---

export type ServerMessage = Readonly<{
  id: string;
  index: number;
  senderTag: string;
  roomTag: string;
  nonce: string;
  ciphertext: string;
  timestamp: number;
}>;

// --- Messages list response ---

type MessagesResponse = Readonly<{
  messages: ReadonlyArray<ServerMessage>;
  has_more: boolean;
  next_since?: number;
}>;

// --- Client factory ---

export const createSpectreClient = (config: ClientConfig) => {
  const serverUrl = config.serverUrl.replace(/\/$/, '');
  const powBits = config.powBits ?? POW_BITS;

  const generateIdentity = (): Readonly<{ publicKey: string; privateKey: string; fingerprint: string }> => {
    const keypair = generateKeypair();
    const publicKey = toHex(keypair.publicKey);
    const privateKey = toHex(keypair.privateKey);
    const fingerprint = computeFingerprint(keypair.publicKey);
    return Object.freeze({ publicKey, privateKey, fingerprint });
  };

  const importIdentity = (privateKeyHex: string): Result<Readonly<{ publicKey: string; privateKey: string; fingerprint: string }>, string> => {
    try {
      const privateKeyBytes = fromHex(privateKeyHex);
      if (privateKeyBytes.length !== 32) {
        return err('Private key must be 32 bytes (64 hex characters)');
      }
      const publicKeyBytes = getPublicKey(privateKeyBytes);
      const publicKey = toHex(publicKeyBytes);
      const fingerprint = computeFingerprint(publicKeyBytes);
      return ok(Object.freeze({ publicKey, privateKey: privateKeyHex, fingerprint }));
    } catch (e) {
      return err(e instanceof Error ? e.message : 'Failed to import identity');
    }
  };

  const createClientRoom = (memberPublicKeyHexes: ReadonlyArray<string>): Readonly<{ roomState: RoomState; shareCode: string }> => {
    const memberBytes = memberPublicKeyHexes.map(fromHex);
    const roomId = generateRoomId();
    const roomState = createRoom(roomId, memberBytes);
    const shareCode = encodeShareCode({
      roomId: toHex(roomState.roomId),
      sharedSecret: toHex(roomState.sharedSecret),
      members: memberPublicKeyHexes as string[],
      currentHead: toHex(roomState.currentHead),
    });
    return Object.freeze({ roomState, shareCode });
  };

  const joinRoom = (shareCode: string, myPublicKeyHex: string): Result<RoomState, string> => {
    const decoded = decodeShareCode(shareCode);
    if (!decoded.ok) return decoded;

    const payload = decoded.value;
    const memberHexes = [...payload.members];
    if (!memberHexes.includes(myPublicKeyHex)) {
      memberHexes.push(myPublicKeyHex);
    }

    const members = memberHexes.map(fromHex);
    const roomState: RoomState = {
      roomId: fromHex(payload.roomId),
      sharedSecret: fromHex(payload.sharedSecret),
      members,
      currentHead: fromHex(payload.currentHead),
      recentHeads: [fromHex(payload.currentHead)],
    };
    return ok(roomState);
  };

  const sendClientMessage = async (
    room: RoomState,
    text: string,
    privateKeyHex: string,
  ): Promise<Result<Readonly<{ id: string; index: number; timestamp: number; updatedRoom: RoomState }>, string>> => {
    const privateKeyBytes = fromHex(privateKeyHex);
    const textEncoder = new TextEncoder();
    const messageBytes = textEncoder.encode(text);
    const result = sendMessage(room, messageBytes, privateKeyBytes, powBits);
    if (!result.ok) return result;

    const { wireMessage, nonce } = result.value;

    // Build binary payload: [senderTag:32][roomTag:32][nonce:16][ciphertext:N]
    const payload = concat(
      wireMessage.senderTag,
      wireMessage.roomTag,
      wireMessage.nonce,
      wireMessage.ciphertext,
    );

    try {
      const response = await fetch(`${serverUrl}/api/v1/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: payload.buffer.slice(payload.byteOffset, payload.byteOffset + payload.byteLength) as ArrayBuffer,
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ error: response.statusText })) as { error?: string };
        return err(errorBody.error ?? `HTTP ${response.status}`);
      }

      const data = await response.json() as { id: string; index: number; timestamp: number };
      const updatedRoom = advanceHead(room, messageBytes);
      return ok(Object.freeze({ ...data, updatedRoom }));
    } catch (e) {
      return err(e instanceof Error ? e.message : 'Network error');
    }
  };

  const subscribe = (
    since: number,
    onMessage: (message: ServerMessage) => void,
  ): (() => void) => {
    const url = `${serverUrl}/api/v1/stream?since=${since}`;
    const eventSource = new EventSource(url);

    eventSource.onmessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data as string) as ServerMessage;
        onMessage(message);
      } catch {
        // Ignore malformed messages
      }
    };

    return () => {
      eventSource.close();
    };
  };

  const sendAnnounce = async (
    room: RoomState,
    privateKeyHex: string,
  ): Promise<Result<Readonly<{ id: string; index: number; timestamp: number; updatedRoom: RoomState }>, string>> => {
    const publicKeyBytes = getPublicKey(fromHex(privateKeyHex));
    const announcePayload = JSON.stringify({ type: 'announce', pubkey: toHex(publicKeyBytes) });
    return sendClientMessage(room, announcePayload, privateKeyHex);
  };

  const fetchAllMessages = async (): Promise<ReadonlyArray<ServerMessage>> => {
    const allMessages: ServerMessage[] = [];
    let since = -1;
    let hasMore = true;

    while (hasMore) {
      try {
        const response = await fetch(`${serverUrl}/api/v1/messages?since=${since}&limit=1000`);
        if (!response.ok) break;

        const data = await response.json() as MessagesResponse;
        const messages = data.messages ?? [];
        allMessages.push(...messages);
        hasMore = data.has_more;

        if (hasMore) {
          since = data.next_since !== undefined
            ? data.next_since
            : (messages.length > 0 ? messages[messages.length - 1].index : since);
        }
      } catch {
        break;
      }
    }

    return Object.freeze(allMessages);
  };

  const getStatus = async (): Promise<Result<ServerStatus, string>> => {
    try {
      const response = await fetch(`${serverUrl}/api/v1/status`);
      if (!response.ok) {
        return err(`HTTP ${response.status}`);
      }
      const data = await response.json() as ServerStatus;
      return ok(data);
    } catch (e) {
      return err(e instanceof Error ? e.message : 'Network error');
    }
  };

  return Object.freeze({
    generateIdentity,
    importIdentity,
    createRoom: createClientRoom,
    joinRoom,
    sendMessage: sendClientMessage,
    sendAnnounce,
    fetchAllMessages,
    subscribe,
    getStatus,
    // Expose utilities for the app
    toHex,
    fromHex,
    computeFingerprint,
  });
};
